import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { useTournaments } from '../../contexts/TournamentContext'
import { useAudio } from '../../contexts/AudioContext'
import UserDisplay from '../UserDisplay/UserDisplay'
import MatchmakingDashboard from '../MatchmakingDashboard/MatchmakingDashboard'
import PlatformStats from '../PlatformStats/PlatformStats'
import LiveGamesPreview from '../LiveGames/LiveGamesPreview'
import ConfirmModal from '../Modal/ConfirmModal'
import QuestAccountModal from '../Modal/QuestAccountModal'

const TIME_CONTROL_PRESETS = {
  classic: {
    label: '30+0',
    description: 'Classical time control - 30 minutes each',
    category: 'Classical',
    config: {
      initial_time: 1800, // 30 minutes in seconds
      increment: 0
    }
  },
  rapid: {
    label: '10+0',
    description: 'Rapid time control - 10 minutes each',
    category: 'Rapid',
    config: {
      initial_time: 600, // 10 minutes in seconds
      increment: 0
    }
  },
  blitz_5_2: {
    label: '5+2',
    description: 'Blitz with 2-second increment',
    category: 'Blitz',
    config: {
      initial_time: 300, // 5 minutes in seconds
      increment: 2
    }
  },
  blitz_3_2: {
    label: '3+2',
    description: 'Fast blitz with 2-second increment',
    category: 'Blitz',
    config: {
      initial_time: 180, // 3 minutes in seconds
      increment: 2
    }
  },
  bullet: {
    label: '1+0',
    description: 'Bullet chess - 1 minute each',
    category: 'Bullet',
    config: {
      initial_time: 60, // 1 minute in seconds
      increment: 0
    }
  }
}

export default function DashboardHome() {
  const navigate = useNavigate()
  const { user, profile: authProfile, isGuest, triggerRegistration, session } = useAuth()
  const {
    createGame,
    joinGame,
    availableGames,
    listAvailableGames,
    games,
    listGames,
    currentGame,
    getGame,
    setCurrentGame,
    getProfile,
    getMatchHistory,
    deleteGame
  } = useGame()

  const { tournaments, listTournaments, joinTournament, leaveTournament } = useTournaments()
  const {
    addMessageHandler,
    removeMessageHandler,
    isConnected,
    joinGame: joinWebSocketGame,
    subscribe
  } = useWebSocket()
  const { playMusic } = useAudio()

  const [loading, setLoading] = useState(false)
  const [timePreset, setTimePreset] = useState('rapid')
  const [gameRated, setGameRated] = useState(!isGuest)
  const [profileStats, setProfileStats] = useState(null)
  
  const [showAbandonModal, setShowAbandonModal] = useState(false)
  const [gameToAbandon, setGameToAbandon] = useState(null)
  
  const [showQuestModal, setShowQuestModal] = useState(false)
  const [showPrivateGameModal, setShowPrivateGameModal] = useState(false)
  const [privateGameUrl, setPrivateGameUrl] = useState('')
  const [currentTime, setCurrentTime] = useState(new Date())

  // Play music on mount
  useEffect(() => {
    playMusic()
  }, [playMusic])

  // Timer for tournament countdowns
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) 
    return () => clearInterval(timer)
  }, [])

  // Listen for matchmaking game_started events
  useEffect(() => {
    if (!subscribe) return

    const handleMessage = (msg) => {
      if (msg.data?.game_id) {
          console.log('Match found! Joining game:', msg.data.game_id)
          handleNavigateToGame(msg.data.game_id)
      }
    }

    console.log('Dashboard subscribing to game_started events')
    return subscribe('game_started', handleMessage)
  }, [subscribe])

  // Load user games and profile
  useEffect(() => {
    if (!user) {
        setProfileStats(null)
        return 
    }
    
    listGames()
    listTournaments()
    
    let isMounted = true
    const loadProfile = async () => {
      try {
        let profileData = authProfile
        if (!profileData && session?.access_token) {
           profileData = await getProfile()
        }
        
        if (isMounted) {
          setProfileStats(profileData)
        }
      } catch (err) {
        console.error('Failed to load profile:', err)
      }
    }
    loadProfile()
    return () => { isMounted = false }
  }, [user?.id, authProfile, session?.access_token])

  // WebSocket Game State Updates
  const refreshTimeoutRef = useRef(null)
  useEffect(() => {
    if (!isConnected) return

    const handleGameStateUpdate = (message) => {
      const gameData = message.data

      // If this is our current game, update it immediately (though we are on dashboard, so maybe less relevant unless we show mini-boards)
      // But more importantly, check if we need to auto-navigate
      if (currentGame && gameData.id === currentGame.id) {
        setCurrentGame(gameData)

        if ((gameData.status === 'pregame' && currentGame.status === 'waiting') ||
            (gameData.status === 'active' && (currentGame.status === 'waiting' || currentGame.status === 'pregame'))) {
          navigate(`/game/${gameData.id}`)
        }
      }

      // Debounce list refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      refreshTimeoutRef.current = setTimeout(() => {
        listGames()
        listAvailableGames()
        refreshTimeoutRef.current = null
      }, 500)
    }

    addMessageHandler('game_state', handleGameStateUpdate)
    return () => {
      removeMessageHandler('game_state')
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current)
    }
  }, [isConnected, currentGame, addMessageHandler, removeMessageHandler, setCurrentGame, listGames, listAvailableGames, navigate])


  const handleCreateGame = useCallback(async (isPrivate = false) => {
    try {
      setLoading(true)

      const myWaitingGames = (games || []).filter(game =>
        (game.status === 'waiting' || game.status === 'pregame') &&
        (game.white_player_id === user?.id || game.black_player_id === user?.id)
      )

      if (myWaitingGames.length >= 3) {
        alert('You can only have 3 open games at a time. Please finish or abandon some games first.')
        return
      }

      const preset = TIME_CONTROL_PRESETS[timePreset] || TIME_CONTROL_PRESETS.rapid
      console.log('Creating game with time control:', preset.config)

      const game = await createGame({
        time_control: preset.config,
        rated: gameRated && !isGuest,
        private: isPrivate
      })

      if (isConnected && game?.id) {
        joinWebSocketGame(game.id)
      }

      if (isPrivate) {
        const gameUrl = `https://www.riftchess.com/game/${game.id}`
        setPrivateGameUrl(gameUrl)
        setShowPrivateGameModal(true)
      } else {
        navigate(`/game/${game.id}`)
      }
    } catch (err) {
      console.error('Failed to create game:', err)
    } finally {
      setLoading(false)
    }
  }, [games, user?.id, timePreset, gameRated, isGuest, createGame, isConnected, joinWebSocketGame, navigate])

  const handleNavigateToGame = useCallback(async (gameId) => {
      navigate(`/game/${gameId}`)
  }, [navigate])

  const handleResumeGame = useCallback(async (gameId) => {
    try {
        setLoading(true)
        await getGame(gameId) // ensure loaded
        navigate(`/game/${gameId}`)
    } catch (err) {
        console.error('Failed to resume game:', err)
    } finally {
        setLoading(false)
    }
  }, [getGame, navigate])

  const handleAbandonGame = (gameId) => {
    setGameToAbandon(gameId)
    setShowAbandonModal(true)
  }

  const confirmAbandonGame = async () => {
    if (!gameToAbandon) return
    try {
      setLoading(true)
      await deleteGame(gameToAbandon)
      await listAvailableGames()
    } catch (err) {
      console.error('Failed to abandon game:', err)
    } finally {
      setLoading(false)
      setGameToAbandon(null)
    }
  }

  const copyPrivateGameUrl = () => {
    navigator.clipboard.writeText(privateGameUrl).then(() => {
      console.log('Link copied to clipboard')
    }).catch(() => {
      console.error('Failed to copy link')
    })
  }

  const handleJoinPrivateGame = () => {
    // Logic to parse game ID from URL and join would go here 
    // For now we just close modal as the user likely created it
    setShowPrivateGameModal(false)
    // If we have a created game ID, go to it
    // But since state is local, we might need to store createdGameId
    // Simple fix: just close. User can click Resume.
  }

  const handleTournamentJoin = async (tournamentId) => {
      if (isGuest) {
        setShowQuestModal(true)
        return
      }
      try {
        setLoading(true)
        await joinTournament(tournamentId)
        await listTournaments()
        if (window.showToast) {
          window.showToast('Joined tournament successfully!', 'success')
        }
      } catch (error) {
        console.error(error)
        if (window.showToast) {
          window.showToast(error.message || 'Failed to join tournament', 'error')
        }
      } finally {
        setLoading(false)
      }
  }

  const handleTournamentLeave = async (tournamentId) => {
      try {
        setLoading(true)
        await leaveTournament(tournamentId)
        await listTournaments()
        if (window.showToast) {
            window.showToast('Left tournament successfully', 'info')
        }
      } catch (error) {
        console.error(error)
        if (window.showToast) {
            window.showToast(error.message || 'Failed to leave tournament', 'error')
        }
      } finally {
        setLoading(false)
      }
  }
  
  const isRegisteredForTournament = (tournamentId) => {
    if (!tournaments || !user?.id) return false;
    const tournament = tournaments.find(t => t.id === tournamentId);
    return tournament ? tournament.is_registered : false;
  }

  
  const ratingDisplay = isGuest ? '0' : (profileStats?.rating ?? '—')
  const gamesWonDisplay = isGuest ? 0 : (profileStats?.games_won ?? 0)
  const tournamentsWonDisplay = isGuest ? 0 : (profileStats?.tournaments_won ?? 0)
  const winRateDisplay = isGuest ? '0%' : (profileStats
    ? `${Math.round(profileStats.win_rate || 0)}%`
    : '0%')
  const displayName = profileStats?.username || 'Loading...'


  return (
    <main className="dashboard-content">


        <div className="dashboard-layout">
          <div className="main-content">
            <section className="tournaments-section">
              <div className="tournaments-top-grid">
                {tournaments && tournaments.length > 0 ? (
                  (() => {
                    // Find the featured tournament
                    const featuredTournament = tournaments.find(t =>
                      (t.status === 'registering' || t.status === 'active') &&
                      t.name.toLowerCase().includes('monthly')
                    ) || tournaments.find(t =>
                      (t.status === 'registering' || t.status === 'active') &&
                      t.name.toLowerCase().includes('weekly')
                    ) || tournaments.find(t =>
                      (t.status === 'registering' || t.status === 'active') &&
                      t.name.toLowerCase().includes('daily')
                    ) || tournaments.find(t =>
                      (!t.max_players || t.max_players > 32)
                    ) || tournaments[0];

                    // Find the RiftChess Invitational tournament
                    const invitationalTournament = tournaments.find(t =>
                      t.name.toLowerCase().includes('riftchess invitational')
                    );

                    const getScheduledStartTime = (tournament) => {
                      return tournament.start_time ? new Date(tournament.start_time) : null;
                    }

                    const getEntryDisplayText = (tournament) => {
                      const isInviteOnly = tournament.name.toLowerCase().includes('invitational');
                      const entryFee = tournament.entry_fee || 0;
                      const isPremiumRequired = tournament.access_type === 'premium';

                      if (isInviteOnly) return 'Invite Only';

                      let entryText = entryFee > 0 ? `$${entryFee} entry` : 'Free';

                      if (isPremiumRequired) {
                        entryText += ', Premium required';
                      }

                      return entryText;
                    }

                    const isBigTournament = !featuredTournament.max_players || featuredTournament.max_players > 32
                    const startTime = isBigTournament ? getScheduledStartTime(featuredTournament) : (featuredTournament.start_time ? new Date(featuredTournament.start_time) : null)
                    const prizePool = featuredTournament.prize_pool?.total_amount ?
          `$${featuredTournament.prize_pool.total_amount}` : "TBD"
                    const isRegistered = isRegisteredForTournament(featuredTournament.id)

                    const formatCountdown = (startTime) => {
                      if (!startTime) return '';
                      const now = currentTime;
                      const start = new Date(startTime);
                      const diff = start - now;

                      if (diff <= 0) return 'Starting soon';

                      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                      if (days > 0) return `${days}d ${hours}h`;
                      if (hours > 0) return `${hours}h ${minutes}m`;
                      if (minutes > 0) return `${minutes}m ${seconds}s`;
                      return `${seconds}s`;
                    };

                    return (
                      <>
                        {/* <div className="tournament-card featured">
                          {isRegistered && (
                            <div className="tournament-badge" style={{backgroundColor: '#84cc16', color: 'black', border: 'none', borderRadius: '4px', padding: '3px 8px'}}>REGISTERED</div>
                          )}
                          <div className="tournament-header" style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start'}}>
                            <h4>{featuredTournament.name.replace(/\\s*\\(bracket\\)|\\s*\\(round_robin\\)|\\s*Sit & Go/gi, '')}</h4>
                            {startTime && (
                              <div className="tournament-countdown">
                                <div>
                                  <span style={{fontSize: '1.1rem', color: '#84cc16', fontWeight: 'bold'}}>
                                    {startTime.getTime() > currentTime.getTime() ? `Starts in ${formatCountdown(startTime)}` : 'Live now!'}
                                  </span>
                                  <span style={{fontSize: '0.9rem', color: '#444', marginLeft: '8px'}}>
                                    @ {startTime.toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{display: 'flex', gap: '2rem'}}>
                            <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                              <div style={{fontSize: '1rem', color: '#444'}}>
                                Entry: <span style={{color: '#ffffff'}}>
                                  {getEntryDisplayText(featuredTournament)}
                                  {featuredTournament.access_type === 'premium' && !featuredTournament.name.toLowerCase().includes('invitational') && (
                                    <span style={{ color: '#ef4444', marginLeft: '2px', fontSize: '1.2rem', fontWeight: 'bold' }}>*</span>
                                  )}
                                </span>
                              </div>
                              <div style={{fontSize: '1rem', color: '#444'}}>Prize: <span style={{color: '#ffffff'}}>{prizePool}</span></div>
                              <div style={{fontSize: '1rem', color: '#444', marginBottom: '1rem'}}>Registered Players: <span style={{color: '#ffffff'}}>{featuredTournament.players_count}{featuredTournament.max_players ? `/${featuredTournament.max_players}` : ''}</span></div>
                            </div>
                          </div>
                          <div className="tournament-actions tournament-actions-desktop" style={{display: 'flex', gap: '0.5rem'}}>
                            <button
                              className={isRegistered ? "action-button outline" : "action-button tournament-register"}
                              style={{width: '70%', backgroundColor: isRegistered ? 'transparent' : '#84cc16', borderColor: isRegistered ? '#dc2626' : '#84cc16', color: isRegistered ? '#dc2626' : '#ffffff', whiteSpace: 'nowrap'}}
                              onClick={() => isRegistered ? handleTournamentLeave(featuredTournament.id) : handleTournamentJoin(featuredTournament.id)}
                              onMouseEnter={(e) => {
                                if (isRegistered) {
                                  e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                                  e.target.style.borderColor = '#b91c1c';
                                  e.target.style.color = '#b91c1c';
                                } else {
                                  e.target.style.backgroundColor = '#65a30d';
                                  e.target.style.borderColor = '#65a30d';
                                  e.target.style.color = '#ffffff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isRegistered) {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#dc2626';
                                  e.target.style.color = '#dc2626';
                                } else {
                                  e.target.style.backgroundColor = '#84cc16';
                                  e.target.style.borderColor = '#84cc16';
                                  e.target.style.color = '#ffffff';
                                }
                              }}
                            >
                              {isRegistered ? 'Unregister' : 'Register Now'}
                            </button>
                            <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '30%'}} onClick={() => navigate(`/tournaments/${featuredTournament.id}`)} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>Details</button>
                          </div>

                          <div className="tournament-mobile-actions">
                            <button
                              className={isRegistered ? "action-button outline" : "action-button tournament-register"}
                              style={{backgroundColor: isRegistered ? 'transparent' : '#84cc16', borderColor: isRegistered ? '#dc2626' : '#84cc16', color: isRegistered ? '#dc2626' : '#ffffff', whiteSpace: 'nowrap'}}
                              onClick={() => isRegistered ? handleTournamentLeave(featuredTournament.id) : handleTournamentJoin(featuredTournament.id)}
                              onMouseEnter={(e) => {
                                if (isRegistered) {
                                  e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                                  e.target.style.borderColor = '#b91c1c';
                                  e.target.style.color = '#b91c1c';
                                } else {
                                  e.target.style.backgroundColor = '#65a30d';
                                  e.target.style.borderColor = '#65a30d';
                                  e.target.style.color = '#ffffff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isRegistered) {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#dc2626';
                                  e.target.style.color = '#dc2626';
                                } else {
                                  e.target.style.backgroundColor = '#84cc16';
                                  e.target.style.borderColor = '#84cc16';
                                  e.target.style.color = '#ffffff';
                                }
                              }}
                            >
                              {isRegistered ? 'Unregister' : 'Register Now'}
                            </button>
                            <button
                              className="action-button outline"
                              style={{borderColor: '#333', color: '#555'}}
                              onClick={() => navigate(`/tournaments/${featuredTournament.id}`)}
                              onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}}
                              onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}
                            >
                              Details
                            </button>
                          </div>
                        </div> */}

                        {invitationalTournament && (
                          <div className="tournament-card past">
                            <div className="tournament-header">
                              <h4 style={{fontSize: '1.3rem', color: '#ffffff', marginBottom: '0.25rem'}}>RiftChess Invitational</h4>
                              <div style={{fontSize: '0.85rem', color: '#888', marginTop: '0.25rem'}}>Past Tournaments</div>
                            </div>
                            <div style={{display: 'flex', gap: '2rem'}}>
                              <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                                <div style={{fontSize: '1rem', color: '#444'}}>Prize: <span style={{color: '#ffffff'}}>{invitationalTournament.prize_pool?.total_amount ? `$${invitationalTournament.prize_pool.total_amount}` : "TBD"}</span></div>
                                <div style={{fontSize: '1rem', color: '#444'}}>Players: <span style={{color: '#ffffff'}}>{invitationalTournament.players_count}{invitationalTournament.max_players ? `/${invitationalTournament.max_players}` : ''}</span></div>
                              </div>
                            </div>
                            <div className="tournament-actions" style={{display: 'flex', gap: '0.5rem'}}>
                              <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '100%'}} onClick={() => navigate(`/tournaments/${invitationalTournament.id}`)} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>View Winners</button>
                            </div>
                          </div>
                        )}
                      </>
                    )
                  })()
                ) : (
                  <>
                    {/* <div className="tournament-card featured">
                      <div className="tournament-badge">FEATURED</div>
                      <div className="tournament-header">
                        <h4>Monthly Championship</h4>
                        <div className="tournament-countdown">Loading...</div>
                      </div>
                      <div className="tournament-actions" style={{display: 'flex', gap: '0.5rem'}}>
                        <button className="action-button tournament-register" style={{width: '70%', backgroundColor: '#84cc16', borderColor: '#84cc16'}} onClick={() => isGuest ? setShowQuestModal(true) : navigate('/tournaments')}>Register Now</button>
                        <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '30%'}} onClick={() => navigate('/tournaments')} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>Details</button>
                      </div>
                    </div> */}
                    <div className="tournament-card past">
                      <div className="tournament-header">
                        <h4 style={{fontSize: '1.3rem', color: '#ffffff', marginBottom: '0.25rem'}}>RiftChess Invitational</h4>
                        <div style={{fontSize: '0.85rem', color: '#888', marginTop: '0.25rem'}}>Past Tournaments</div>
                      </div>
                      <div className="tournament-actions" style={{display: 'flex', gap: '0.5rem'}}>
                        <button className="action-button outline" style={{borderColor: '#333', color: '#555', width: '100%'}} onClick={() => navigate('/tournaments')} onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}} onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}>View Winners</button>
                      </div>
                    </div>
                  </>
                )}
              </div>

            </section>

            <section className="games-section">
              <div className="games-grid-new">
                <div className="game-card user-stats">
                  <div className="user-stats-header">
                    {profileStats ? (
                      <span
                        onClick={() => navigate(`/user/${profileStats.username}`)}
                        style={{ cursor: 'pointer', display: 'inline-block' }}
                      >
                        <UserDisplay user={profileStats} showTitleBadges={true} showStatusBadges={true} forceWhite={true} context="hero" />
                      </span>
                    ) : (
                      <span className="username-white">{displayName}</span>
                    )}
                  </div>
                  <div className="username-separator"></div>
                  <div className="user-stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Rating</span>
                      <span className="stat-number">{ratingDisplay}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Games Won</span>
                      <span className="stat-number">{gamesWonDisplay}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Tournaments Won</span>
                      <span className="stat-number">{tournamentsWonDisplay}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Win Rate</span>
                      <span className="stat-number">{winRateDisplay}</span>
                    </div>
                  </div>
                </div>

                <div className="game-card create-game">
                  <MatchmakingDashboard onGameFound={handleNavigateToGame} />
                </div>

                <div className="game-card create-private">
                  <h4>Private Game</h4>
                  <p>Create a private game to share with friends</p>

                  <button
                    className="action-button outline"
                    onClick={() => handleCreateGame(true)}
                    disabled={loading}
                    style={{borderColor: 'var(--accent-secondary)', color: 'var(--accent-secondary)', backgroundColor: 'black'}}
                    onMouseEnter={(e) => {e.target.style.backgroundColor = 'var(--accent-secondary)'; e.target.style.color = 'black'}}
                    onMouseLeave={(e) => {e.target.style.backgroundColor = 'black'; e.target.style.color = 'var(--accent-secondary)'}}
                  >
                    {loading ? 'Creating...' : 'Create Private Game'}
                  </button>

                  <div style={{borderTop: '1px solid #333', margin: '1rem 0'}}></div>

                  <div className="games-tabbed">
                  <div className="games-header">
                    <h3>My Games</h3>
                  </div>

                  <div className="games-content" style={{minHeight: '120px', maxHeight: '120px', overflow: 'auto'}}>
                    <div className="game-list">
                    {(() => {
                      const now = new Date()
                      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

                      // Get all user's games including private waiting games
                      const myActiveGames = (games || []).filter(game => {
                        const isRelevantStatus = game.status === 'active' || game.status === 'pregame' || game.status === 'waiting'
                        const isRecent = new Date(game.updated_at || game.created_at) > sevenDaysAgo
                        return isRelevantStatus && isRecent
                      })

                      // Sort by most recent first, limit to 2
                      const myGames = myActiveGames
                        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                        .slice(0, 2)

                      if (myGames.length === 0) {
                        return <p className="no-items">No games</p>
                      }

                      const copyGameLink = (gameId) => {
                        const gameUrl = `https://www.riftchess.com/game/${gameId}`
                        navigator.clipboard.writeText(gameUrl).then(() => {
                          console.log('Game link copied to clipboard')
                        }).catch(() => {
                          console.error('Failed to copy game link')
                        })
                      }

                      return myGames.map(game => (
                        <div key={game.id} className="game-item-mini">
                          {game.status === 'waiting' ? (
                            <>
                              <div className="game-info-row">
                                <div>Waiting for opponent</div>
                                {game.private && (
                                  <div className="game-private-badge">PRIVATE</div>
                                )}
                                <div className={`game-rating-badge ${game.rated && !game.tournament_id ? 'ranked' : 'non-ranked'}`}>
                                  {game.rated && !game.tournament_id ? 'Ranked' : 'Non-Ranked'}
                                </div>
                              </div>
                              <div className="game-actions-row">
                                <button
                                  className="resume-btn"
                                  onClick={() => handleResumeGame(game.id)}
                                  disabled={loading}
                                  title="Resume game"
                                >
                                  Resume
                                </button>
                                {game.private && (
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyGameLink(game.id)}
                                    disabled={loading}
                                    title="Copy game link"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                                    </svg>
                                  </button>
                                )}
                                <button
                                  className="abandon-btn"
                                  onClick={() => handleAbandonGame(game.id)}
                                  disabled={loading}
                                  title="Close game"
                                >
                                  ×
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="game-info-row">
                                <div>
                                  vs {game.white_player_id === user?.id
                                    ? <UserDisplay user={game.black_player} showTitleBadges={false} showStatusBadges={true} />
                                    : <UserDisplay user={game.white_player} showTitleBadges={false} showStatusBadges={true} />}
                                </div>
                                {game.private && (
                                  <div className="game-private-badge">PRIVATE</div>
                                )}
                                <div className={`game-rating-badge ${game.rated && !game.tournament_id ? 'ranked' : 'non-ranked'}`}>
                                  {game.rated && !game.tournament_id ? 'Ranked' : 'Non-Ranked'}
                                </div>
                              </div>
                              <div className="game-actions-row">
                                <button
                                  className="resume-btn"
                                  onClick={() => handleResumeGame(game.id)}
                                >
                                  Resume
                                </button>
                                {game.private && (
                                  <button
                                    className="copy-btn"
                                    onClick={() => copyGameLink(game.id)}
                                    title="Copy game link"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ))
                        })()}
                    </div>
                  </div>
                  </div>
                </div>

              </div>
            </section>

            {/* Stats and Live Games Row */}
            <section className="stats-live-section">
              <div className="stats-live-container">
                <div className="stats-column">
                  <PlatformStats />
                </div>
                <LiveGamesPreview onViewSpectate={() => navigate('/spectate')} />
                {profileStats?.account_type !== 'premium' && (
                <div className="premium-upgrade-column">
                  <div className="game-card premium-upgrade">
                    <div className="premium-header">
                      <h4>Upgrade to Premium <span className="premium-star-inline">★</span></h4>
                    </div>
                    <div className="premium-benefits">
                      <ul>
                        <li>FREE entry to all tournaments<br/><small style={{color: '#666'}}>(Monthly, Weekly and Daily tournaments)</small></li>
                        <li>Unlimited game history</li>
                        <li>Custom themes</li>
                        <li>Enhanced player profiles</li>
                      </ul>
                    </div>
                    <div className="free-trial-banner" style={{color: 'var(--accent-green)', fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '4px', textAlign: 'center'}}>
                      30 days FREE!
                    </div>
                    <div className="cancel-anytime" style={{color: '#666', fontSize: '0.75rem', textAlign: 'center', marginBottom: '8px'}}>
                      (Cancel anytime)
                    </div>
                    <div className="premium-pricing">
                      <div className="pricing-option">
                        <div className="price-label">Monthly</div>
                        <div className="price-value">$7 / month</div>
                      </div>
                      <div className="pricing-option">
                        <div className="price-label">Yearly</div>
                        <div className="price-value">$70 / year</div>
                      </div>
                    </div>
                    <button
                      className="upgrade-button"
                      onClick={() => navigate('/premium')}
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
                )}
              </div>
            </section>

          </div>

        </div>

      <ConfirmModal
        isOpen={showAbandonModal}
        onClose={() => setShowAbandonModal(false)}
        onConfirm={confirmAbandonGame}
        title="Abandon Game"
        message="Are you sure you want to abandon this game? This action cannot be undone."
        confirmText="Abandon"
        cancelText="Cancel"
        type="danger"
      />

      <QuestAccountModal
        isOpen={showQuestModal}
        onClose={() => setShowQuestModal(false)}
        onCreateAccount={() => {
            setShowQuestModal(false)
            triggerRegistration()
        }}
      />

      {/* Private Game Modal */}
      {showPrivateGameModal && (
        <div className="modal-overlay" onClick={() => setShowPrivateGameModal(false)}>
          <div className="modal-content private-game-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Private Game Created!</h3>
              <button className="modal-close" onClick={() => setShowPrivateGameModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Share this link with your friend to invite them:</p>
              <div className="game-url-container">
                <input
                  type="text"
                  value={privateGameUrl}
                  readOnly
                  className="game-url-input"
                />
                <button
                  className="copy-button"
                  onClick={copyPrivateGameUrl}
                  title="Copy to clipboard"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="action-button primary"
                onClick={handleJoinPrivateGame}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Explore More Tournaments - Bottom Section */}
      <div className="tournament-explore">
        <h4>Explore More Tournaments</h4>
        <p>Discover the full range of RiftChess tournaments and Sit & Go events available. From quick 8-player brackets to massive scheduled championships.</p>

        <div className="explore-features">
          <div className="feature-row">
            <strong>Sit & Go Tournaments</strong>
            <span>Start every few minutes when full</span>
          </div>
          <div className="feature-row">
            <strong>Scheduled Championships</strong>
            <span>Daily, weekly, and monthly events</span>
          </div>
          <div className="feature-row">
            <strong>Multiple Formats</strong>
            <span>Bracket elimination and round-robin</span>
          </div>
          <div className="feature-row">
            <strong>Real Prizes</strong>
            <span>Compete for guaranteed prize pools</span>
          </div>
        </div>

        <button
          className="action-button outline"
          style={{borderColor: '#333', color: '#555'}}
          onMouseEnter={(e) => {e.target.style.backgroundColor = '#333'; e.target.style.borderColor = '#333'; e.target.style.color = '#ffffff'}}
          onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = '#333'; e.target.style.color = '#555'}}
          onClick={() => navigate('/tournaments')}
        >
          View All Tournaments
        </button>
      </div>
    </main>
  )
}
